import React, { ChangeEvent, FocusEvent, useEffect, useState } from 'react';
import { FormControl, Button, TextField, Typography } from '@mui/material';
import styles from '../../index.module.scss';
import BaseModal from '../shared/components/base.modal';
import apiClient from '../shared/auth.interceptor';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { IFirm, setCurrentFirm } from '../shared/redux/firmSlicer';
import { setCurrentFirmList } from '../shared/redux/firmListSlicer';
import { RootState } from '../../store';
import { errorToast } from '../shared/toast.service';
import { setCurrentCompanyList } from '../shared/redux/companySlicer';

interface AddNewFirmModalProps {
  open: boolean;
  edit: boolean;
  handleClose: () => void;
  onSuccess: () => void;
}

interface FormErrors {
  [key: string]: string | undefined;
}

interface FormData {
  firmName: string;
}

const AddNewFirmModal: React.FC<AddNewFirmModalProps> = ({ open, edit, handleClose, onSuccess }) => {
  const currentFirm = useSelector((state: RootState) => state.currentFirm.currentFirm);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<{ firmName: boolean }>({
    firmName: false
  });
  const [firmNameLength, setFirmNameLength] = useState<number>(0);
  const [formData, setFormData] = useState<FormData>({ firmName: '' });
  const dispatch = useDispatch();
  const validateField = (name: keyof FormData, value: string): void => {
    value = value.trim();
    let newErrors: FormErrors = { ...errors };
    if (name === 'firmName') {
      if (!value) {
        setFirmNameLength(0);
        newErrors.firmName = 'Please enter the name';
      } else if (value.length < 3) {
        newErrors.firmName = 'Firm name must be more than 3 characters';
      } else if (value.length > 30) {
        newErrors.firmName = 'Firm name must be less than 30 characters';
      } else {
        delete newErrors.firmName;
      }
      if (value && value.length) {
        setFirmNameLength(value.length);
      }
    }

    setErrors(newErrors);
  };
  const handleInputChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = event.target;
    setFormData({ ...formData, [name]: value });
    validateField(name as keyof FormData, value);
  };
  const resetForm = () => {
    setFormData({ firmName: '' });
    setErrors({});
    setTouched({ firmName: false });
  };
  const handleModalClose = () => {
    setFirmNameLength(0);
    resetForm();
    handleClose();
  };
  const handleInputBlur = (event: FocusEvent<HTMLInputElement>): void => {
    const { name } = event.target;
    setTouched({ ...touched, [name]: true });
    validateField(name as keyof FormData, event.target.value);
  };
  const handleUpdate = async () => {
    if (saving) return;
    const editedFirmId = currentFirm?._id;
    if (edit && !editedFirmId) return;
    if (edit && formData.firmName === currentFirm?.name) {
      errorToast('Firm name is the same');
      return;
    }
    setSaving(true);
    try {
      let firms: IFirm[];
      let selectedId: string | undefined;
      if (edit) {
        const response = await apiClient.put<IFirm[]>(`/v1/firm/${editedFirmId}`, formData);
        firms = response.data;
        selectedId = editedFirmId;
      } else {
        const response = await apiClient.post<{ firms: IFirm[]; createdFirmId: string }>(
          '/v1/firm?includeCreatedId=true', formData
        );
        firms = response.data.firms;
        selectedId = response.data.createdFirmId;
      }
      const selected = firms.find(firm => firm._id === selectedId);
      if (!selected) throw new Error('Saved firm is missing from the response');
      dispatch(setCurrentFirmList(firms));
      dispatch(setCurrentCompanyList(selected.companies || []));
      dispatch(setCurrentFirm(selected));
      toast.success(`Firm ${edit ? 'Name updated' : 'added'} successfully`);
      resetForm();
      onSuccess();
    } catch {
      errorToast(`Failed to ${edit ? 'update' : 'add'} firm`);
      handleModalClose();
    } finally {
      setSaving(false);
    }
  };
  useEffect(() => {
    if (edit) {
      setFormData({ firmName: currentFirm?.name || '' });
      setFirmNameLength(currentFirm?.name?.length || 0);
    }
  }, [edit, currentFirm]);
  const disableSendButton = Object.keys(errors).length > 0 || !formData.firmName;
  return (
    <BaseModal open={open} handleClose={handleModalClose}>
      <img alt={'Logo'} src="/logo120.svg" />
      <p className="my-5">{edit ? 'Edit a firm name' : 'Create a new firm'}</p>
      <FormControl fullWidth margin="normal">
        <TextField
          fullWidth
          error={Boolean(errors.firmName && touched.firmName)}
          helperText={touched.firmName && errors.firmName}
          label="Firm name"
          type="text"
          name="firmName"
          value={formData.firmName}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          variant="outlined"
          required
          inputProps={{ maxLength: 30 }}
        />
        <Typography
          variant="caption"
          sx={{
            textAlign: 'right',
            marginTop: '2px',
            marginRight: '0.2rem',
            fontSize: '0.7rem',
            color: styles.mainGrey
          }}
        >
          {firmNameLength}/30
        </Typography>
      </FormControl>
      <Button
        disabled={disableSendButton || saving}
        onClick={handleUpdate}
        variant="contained"
        sx={{ mt: 2, width: '100%', bgcolor: styles.mainBlue }}
      >
        {edit ? 'Update' : 'Create'}
      </Button>
    </BaseModal>
  );
};

export default AddNewFirmModal;
